import { useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  HomeOutlined,
  MailOutlined,
  PhoneOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Button, Checkbox, DatePicker, Input, Select, message } from "antd";
import { useAuth } from "../contexts/AuthContext";
import { submitDemoRequest } from "../services/demoRequestService";
import { Footer } from "../components/care/CareLayout";
import { PublicNav } from "./care/LandingPage";

const roleOptions = [
  {
    value: "CARE_FACILITY",
    title: "Managing a Care Facility",
    description: "Adult Family Home, Assisted Living Facility, or care team operations.",
    icon: <HomeOutlined />,
  },
  {
    value: "CARE_WORKER",
    title: "For my Care Giving Career",
    description: "Schedules, daily notes, timesheets, and care worker profile tools.",
    icon: <UserOutlined />,
  },
  {
    value: "OTHER",
    title: "Exploring Olreadia Care",
    description: "I want to learn which Care workspace setup fits best.",
    icon: <CalendarOutlined />,
  },
];

const facilityTypeOptions = [
  { value: "Adult Family Home", label: "Adult Family Home" },
  { value: "Assisted Living Facility", label: "Assisted Living Facility" },
  { value: "Care Agency", label: "Care Agency" },
  { value: "Independent Care Worker", label: "Independent Care Worker" },
  { value: "Other", label: "Other" },
];

const teamSizeOptions = [
  { value: "1-5", label: "1-5 people" },
  { value: "6-15", label: "6-15 people" },
  { value: "16-50", label: "16-50 people" },
  { value: "50+", label: "50+ people" },
];

const timelineOptions = [
  { value: "This week", label: "This week" },
  { value: "This month", label: "This month" },
  { value: "Next 3 months", label: "Next 3 months" },
  { value: "Just researching", label: "Just researching" },
];

const interestOptions = [
  "Resident assessments",
  "Negotiated care plans",
  "Schedules",
  "Clocking in and out",
  "Timesheets",
  "Daily notes",
  "Documents and downloads",
  "Staff payments",
  "Find and hire caregivers",
  "AI-assisted care workflows",
  "Workspace billing",
  "Care facility website",
];

const initialForm = {
  userType: "",
  name: "",
  email: "",
  phone: "",
  organizationName: "",
  facilityType: "",
  teamSize: "",
  timeline: "",
  preferredDemoAt: null,
  interests: [],
  message: "",
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const demoStepCount = 6;

export default function BookDemoPage() {
  const { isAuthenticated, loading } = useAuth();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submittedRequest, setSubmittedRequest] = useState(null);

  const selectedRole = useMemo(
    () => roleOptions.find((option) => option.value === form.userType),
    [form.userType],
  );

  if (!loading && isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const canContinue = () => {
    if (step === 0) return Boolean(form.userType);
    if (step === 1) return Boolean(form.email && emailPattern.test(form.email));
    return true;
  };

  const nextStep = () => {
    if (!canContinue()) {
      message.warning(step === 0 ? "Choose how you want to use Olreadia Care." : "Enter a valid email address.");
      return;
    }
    setStep((current) => Math.min(current + 1, demoStepCount - 1));
  };

  const submit = async () => {
    if (!canContinue()) {
      message.warning("Enter a valid email address before submitting.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await submitDemoRequest({
        ...form,
        name: form.name.trim() || null,
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        organizationName: form.organizationName.trim() || null,
        preferredDemoAt: form.preferredDemoAt ? form.preferredDemoAt.toISOString() : null,
        message: form.message.trim() || null,
      });
      setSubmittedRequest(result);
      message.success("Demo request submitted.");
    } catch (error) {
      message.error(error?.message || "Could not submit the demo request.");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    if (submittedRequest) {
      return (
        <div className="demo-success">
          <CheckCircleOutlined />
          <h1>Demo request received</h1>
          <p>We saved your request for Olreadia Care and will follow up using the email you provided.</p>
          <div className="demo-success-meta">
            <span>Request</span>
            <strong>{submittedRequest.id}</strong>
          </div>
          <div className="demo-actions">
            <Link className="btn secondary" to="/">
              <ArrowLeftOutlined /> Back to Olreadia Care
            </Link>
            <Link className="btn primary" to="/get-started">
              Get Started <ArrowRightOutlined />
            </Link>
          </div>
        </div>
      );
    }

    if (step === 0) {
      return (
        <>
          <div className="demo-section-heading">
            <span>Your path</span>
            <h1>How will you use Olreadia Care?</h1>
            <p>Pick the closest match so the demo is shaped around the work you actually need to do.</p>
          </div>
          <div className="demo-option-grid">
            {roleOptions.map((option) => (
              <button
                type="button"
                className={`demo-option ${form.userType === option.value ? "is-selected" : ""}`}
                onClick={() => updateField("userType", option.value)}
                key={option.value}
              >
                <span>{option.icon}</span>
                <strong>{option.title}</strong>
                <small>{option.description}</small>
              </button>
            ))}
          </div>
        </>
      );
    }

    if (step === 1) {
      return (
        <>
          <div className="demo-section-heading">
            <span>Contact</span>
            <h1>Where should we reach you?</h1>
            <p>Phone is optional. Email is required for confirmation and follow-up.</p>
          </div>
          <div className="demo-form-grid">
            <label>
              <span>Name</span>
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="Your name"
                value={form.name}
                onChange={(event) => updateField("name", event.target.value)}
              />
            </label>
            <label>
              <span>Email</span>
              <Input
                size="large"
                prefix={<MailOutlined />}
                placeholder="you@example.com"
                value={form.email}
                onChange={(event) => updateField("email", event.target.value)}
              />
            </label>
            <label>
              <span>Phone optional</span>
              <Input
                size="large"
                prefix={<PhoneOutlined />}
                placeholder="Best callback number"
                value={form.phone}
                onChange={(event) => updateField("phone", event.target.value)}
              />
            </label>
            <label>
              <span>Organization or facility</span>
              <Input
                size="large"
                prefix={<HomeOutlined />}
                placeholder="Facility or organization name"
                value={form.organizationName}
                onChange={(event) => updateField("organizationName", event.target.value)}
              />
            </label>
          </div>
        </>
      );
    }

    if (step === 2) {
      return (
        <>
          <div className="demo-section-heading">
            <span>Care setting</span>
            <h1>Tell us about the setting.</h1>
            <p>{selectedRole?.title || "Olreadia Care"} demo details help us prepare the right walkthrough.</p>
          </div>
          <div className="demo-form-grid">
            <label>
              <span>Facility or work type</span>
              <Select
                size="large"
                placeholder="Select type"
                value={form.facilityType || undefined}
                options={facilityTypeOptions}
                onChange={(value) => updateField("facilityType", value)}
              />
            </label>
            <label>
              <span>Team size</span>
              <Select
                size="large"
                placeholder="Select size"
                value={form.teamSize || undefined}
                options={teamSizeOptions}
                onChange={(value) => updateField("teamSize", value)}
              />
            </label>
          </div>
        </>
      );
    }

    if (step === 3) {
      return (
        <>
          <div className="demo-section-heading">
            <span>Timing</span>
            <h1>When should we connect?</h1>
            <p>Choose a rough timeline and an optional preferred date and time.</p>
          </div>
          <div className="demo-form-grid">
            <label>
              <span>Timeline</span>
              <Select
                size="large"
                placeholder="Select timeline"
                value={form.timeline || undefined}
                options={timelineOptions}
                onChange={(value) => updateField("timeline", value)}
              />
            </label>
            <label>
              <span>Preferred date and time</span>
              <DatePicker
                size="large"
                showTime={{ format: "h:mm A", minuteStep: 15 }}
                format="MMM D, YYYY h:mm A"
                placeholder="Select date and time"
                value={form.preferredDemoAt}
                disabledDate={(current) => current && current.endOf("day").valueOf() < Date.now()}
                onChange={(value) => updateField("preferredDemoAt", value)}
                style={{ width: "100%" }}
              />
            </label>
          </div>
        </>
      );
    }

    if (step === 4) {
      return (
        <>
          <div className="demo-section-heading">
            <span>Focus</span>
            <h1>What are you interested in?</h1>
            <p>Select the care tools that matter most right now.</p>
          </div>
          <div className="demo-interest-panel">
            <span>Interested in</span>
            <Checkbox.Group
              value={form.interests}
              onChange={(value) => updateField("interests", value)}
            >
              {interestOptions.map((interest) => (
                <Checkbox value={interest} key={interest}>{interest}</Checkbox>
              ))}
            </Checkbox.Group>
          </div>
        </>
      );
    }

    return (
      <>
        <div className="demo-section-heading">
          <span>Note</span>
          <h1>Anything else?</h1>
          <p>Add any context that would help us prepare. You can also leave this blank.</p>
        </div>
        <label className="demo-message-field">
          <span>Optional note</span>
          <Input.TextArea
            rows={5}
            placeholder="Tell us about your residents, team, assessments, billing, website, or care workflow goals."
            value={form.message}
            onChange={(event) => updateField("message", event.target.value)}
          />
        </label>
      </>
    );
  };

  return (
    <div className="page public-page book-demo-page">
      <PublicNav />
      <main className="book-demo-main">
        <section className="book-demo-shell">
          <div className="book-demo-card">
            <div className="demo-card-hero">
              <div>
                <span className="demo-card-kicker"><CalendarOutlined /> Olreadia Care demo</span>
                <h1>Book a guided walkthrough</h1>
                <p>Tell us what you manage today and we will shape the demo around your care workflow.</p>
              </div>
            </div>
            <div className="demo-progress" aria-label="Demo request progress">
              <div>
                <span style={{ width: `${((step + 1) / demoStepCount) * 100}%` }} />
              </div>
              <strong>Step {step + 1} of {demoStepCount}</strong>
            </div>
            {renderStep()}
            {!submittedRequest && (
              <div className="demo-actions">
                <Button
                  size="large"
                  icon={<ArrowLeftOutlined />}
                  disabled={step === 0 || submitting}
                  onClick={() => setStep((current) => Math.max(current - 1, 0))}
                >
                  Back
                </Button>
                {step < demoStepCount - 1 ? (
                  <Button
                    size="large"
                    type="primary"
                    icon={<ArrowRightOutlined />}
                    onClick={nextStep}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    size="large"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={submitting}
                    onClick={submit}
                  >
                    Submit request
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer publicLinks />
    </div>
  );
}
